import { createContext, Dispatch, PropsWithChildren, useContext, useReducer } from 'react'

import { FirestoreSettings } from 'views/AdjustersHub/interfces'

const adjusterDefaultStateContext = {
  claimsSearch: '',
  isOfflineRequestLoading: false,
  firestoreSettings: undefined,
}

interface AdjusterState {
  claimsSearch: string
  isOfflineRequestLoading: boolean
  firestoreSettings?: FirestoreSettings
}

type ActionReturns =
  | { type: 'claimsSearch'; claimsSearch: string }
  | { type: 'setOfflineRequestLoading'; isOfflineRequestLoading: boolean }
  | { type: 'setFirestoreSettings'; firestoreSettings: FirestoreSettings }

type DispatchAdjusterActions = Dispatch<ActionReturns>

const AdjusterContext = createContext<AdjusterState | undefined>(undefined)
AdjusterContext.displayName = 'AdjusterContext'

const AdjusterDispatchContext = createContext<DispatchAdjusterActions | undefined>(undefined)
AdjusterDispatchContext.displayName = 'AdjusterDispatchContext'

function adjusterReducer(state: AdjusterState, action: ActionReturns): AdjusterState {
  switch (action.type) {
    case 'claimsSearch':
      return {
        ...state,
        claimsSearch: action.claimsSearch,
      }
    case 'setOfflineRequestLoading':
      return {
        ...state,
        isOfflineRequestLoading: action.isOfflineRequestLoading,
      }
    case 'setFirestoreSettings':
      return {
        ...state,
        firestoreSettings: action.firestoreSettings,
      }
    default:
      return state
  }
}

function AdjusterProvider({ children }: PropsWithChildren): JSX.Element {
  const [state, dispatch] = useReducer(adjusterReducer, adjusterDefaultStateContext)

  return (
    <AdjusterContext.Provider value={state}>
      <AdjusterDispatchContext.Provider value={dispatch}>
        {children}
      </AdjusterDispatchContext.Provider>
    </AdjusterContext.Provider>
  )
}

const useAdjusterState = (): AdjusterState => {
  const context = useContext(AdjusterContext)
  if (context === undefined) {
    throw new Error('useAdjuster hook must be used within a AdjusterProvider')
  }

  return context
}

const useAdjusterDispatch = (): DispatchAdjusterActions => {
  const context = useContext(AdjusterDispatchContext)

  if (context === undefined) {
    throw new Error('useAdjuster hook must be used within a AdjusterProvider')
  }

  return context
}

export { AdjusterProvider, useAdjusterState, useAdjusterDispatch, AdjusterState }
